import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useGetUserID } from "../hooks/useGetUserID";
import RecipeIngredientInput from "../components/RecipeIngredientInput";
import { API_BASE_URL } from '../config';
import { useCookies } from 'react-cookie';
import RecipeImage from '../components/RecipeImage';
import '../styles/createrecipe.css'; // Import the shared CSS

export default function EditRecipe() {
    const { recipeId } = useParams();
    const navigate = useNavigate();
    const userID = useGetUserID();
    const [cookies] = useCookies(["access_token"]);
    
    // Helper function to get auth token from cookies or localStorage
    const getAuthToken = () => {
        return cookies.access_token || localStorage.getItem('auth_token');
    };
    
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    
    // State for instruction images
    const [instructionImages, setInstructionImages] = useState({});
    const [instructionImagePreviews, setInstructionImagePreviews] = useState({});
    
    // State for ingredients photo
    const [selectedIngredientsPhoto, setSelectedIngredientsPhoto] = useState(null);
    const [ingredientsPhotoPreview, setIngredientsPhotoPreview] = useState(null);
    
    // State for photo generation
    const [isGeneratingPhotos, setIsGeneratingPhotos] = useState(false);
    const [photoGenerationError, setPhotoGenerationError] = useState('');
    const [photoGenerationSuccess, setPhotoGenerationSuccess] = useState('');
    const [photoGenerationAvailable, setPhotoGenerationAvailable] = useState(false);
    
    // AI Image Quota state
    const [aiImageQuota, setAiImageQuota] = useState(null);
    const [imageQuotaLoading, setImageQuotaLoading] = useState(false);
    
    // Display text generation state
    const [isGeneratingDisplayText, setIsGeneratingDisplayText] = useState(false);
    const [displayTextError, setDisplayTextError] = useState('');
    const [displayTextSuccess, setDisplayTextSuccess] = useState('');

    // Add available categories constant - South African themed
    const AVAILABLE_CATEGORIES = [
        'flavoured chicken',
        'break a egg breakfasts',
        'cape malay indian',
        'south african',
        'baked goods',
        'braai & side dishes',
        'vegetarian',
        'decadently healthy desserts',
        'healthy classics'
    ];

    // Map category values to display names
    const categoryDisplayNames = {
        'flavoured chicken': 'Flavoursome Chicken',
        'break a egg breakfasts': 'Break-a-Egg Breakfasts',
        'cape malay indian': 'Cape Malay & Indian Spice',
        'south african': 'Proudly South African',
        'baked goods': 'Oven-Fresh Baked Goods',
        'braai & side dishes': 'Braai & Sides',
        'vegetarian': 'Garden Fresh Veggie',
        'decadently healthy desserts': 'Decadently Healthy Desserts',
        'healthy classics': 'Healthy Classics'
    };

    const [recipe, setRecipe] = useState({
        name: "",
        servings: 1,
        ingredients: [{
            name: "",
            amount: "",
            unit: "g",
            customUnit: "",
            displayText: "",
            preparation: ""
        }],
        instructions: [{ text: '', imageUrl: '' }],
        imageUrl: "",
        ingredientsPhotoUrl: "",
        cookingTime: 0,
        userOwner: userID || "",
        isVegan: false,
        isVegetarian: false,
        categories: []
    });

    useEffect(() => {
        const fetchRecipe = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/recipes/${recipeId}`);
                const recipeData = response.data;
                // Convert instructions to new format if needed
                const convertedInstructions = recipeData.instructions?.map(instruction => {
                    if (typeof instruction === 'string') {
                        return { text: instruction, imageUrl: '' };
                    }
                    return instruction;
                }) || [{ text: '', imageUrl: '' }];

                // Set up instruction image previews for existing images
                const imagePreviews = {};
                convertedInstructions.forEach((instruction, index) => {
                    if (instruction.imageUrl) {
                        imagePreviews[index] = instruction.imageUrl;
                    }
                });
                setInstructionImagePreviews(imagePreviews);

                // Set up ingredients photo preview if it exists
                if (recipeData.ingredientsPhotoUrl) {
                    setIngredientsPhotoPreview(recipeData.ingredientsPhotoUrl);
                }

                // Ensure all fields are properly initialized, including new optional display fields
                const normalizedIngredients = (recipeData.ingredients || []).map(ing => ({
                    name: ing.name || "",
                    amount: ing.amount || "",
                    unit: ing.unit || "g",
                    customUnit: ing.customUnit || "",
                    displayText: ing.displayText || "",
                    preparation: ing.preparation || ""
                }));
                
                setRecipe({
                    name: recipeData.name || "",
                    servings: recipeData.servings || 1,
                    ingredients: normalizedIngredients.length > 0 ? normalizedIngredients : [{
                        name: "",
                        amount: "",
                        unit: "g",
                        customUnit: "",
                        displayText: "",
                        preparation: ""
                    }],
                    instructions: convertedInstructions,
                    imageUrl: recipeData.imageUrl || "",
                    ingredientsPhotoUrl: recipeData.ingredientsPhotoUrl || "",
                    cookingTime: recipeData.cookingTime || 0,
                    userOwner: recipeData.userOwner || userID || "",
                    isVegan: recipeData.isVegan || false,
                    isVegetarian: recipeData.isVegetarian || false,
                    categories: recipeData.categories || []
                });
                setImagePreview(recipeData.imageUrl);
            } catch (error) {
                console.error("Error fetching recipe:", error);
                setError("Error fetching recipe details");
            }
        };

        const checkPhotoGenerationAvailability = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/recipes/photo-generation/status`);
                setPhotoGenerationAvailable(response.data.available);
            } catch (error) {
                console.error("Error checking photo generation availability:", error);
                setPhotoGenerationAvailable(false);
            }
        };

        const fetchAiImageQuota = async () => {
            try {
                setImageQuotaLoading(true);
                const response = await axios.get(`${API_BASE_URL}/recipes/ai-image-quota`, {
                    headers: {
                        Authorization: `Bearer ${getAuthToken()}`
                    }
                });
                if (response.data.success) {
                    setAiImageQuota(response.data.quota);
                }
            } catch (error) {
                console.error('Error fetching AI image quota:', error);
                // Don't show error to user, just log it
            } finally {
                setImageQuotaLoading(false);
            }
        };

        fetchRecipe();
        checkPhotoGenerationAvailability();
        fetchAiImageQuota();
    }, [recipeId]);

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;
        if (type === 'checkbox') {
            setRecipe({ ...recipe, [name]: checked });
        } else {
            setRecipe({ ...recipe, [name]: value });
        }
    };

    const handleIngredientChange = (index, field, value) => {
        const newIngredients = [...recipe.ingredients];
        newIngredients[index] = {
            ...newIngredients[index],
            [field]: value
        };
        setRecipe({ ...recipe, ingredients: newIngredients });
    };

    const addIngredient = () => {
        setRecipe({
            ...recipe,
            ingredients: [
                ...recipe.ingredients,
                { name: "", amount: "", unit: "g", customUnit: "", displayText: "", preparation: "" }
            ]
        });
    };

    const removeIngredient = (index) => {
        const newIngredients = recipe.ingredients.filter((_, i) => i !== index);
        setRecipe({ ...recipe, ingredients: newIngredients });
    };

    const handleInstructionChange = (index, field, value) => {
        const newInstructions = [...recipe.instructions];
        newInstructions[index] = {
            ...newInstructions[index],
            [field]: value
        };
        setRecipe({ ...recipe, instructions: newInstructions });
    };

    const addInstruction = () => {
        setRecipe({
            ...recipe,
            instructions: [...recipe.instructions, { text: '', imageUrl: '' }]
        });
    };

    const removeInstruction = (index) => {
        const newInstructions = recipe.instructions.filter((_, i) => i !== index);
        setRecipe({ ...recipe, instructions: newInstructions });
        
        // Clean up associated image states
        const newInstructionImages = { ...instructionImages };
        const newInstructionImagePreviews = { ...instructionImagePreviews };
        delete newInstructionImages[index];
        delete newInstructionImagePreviews[index];
        
        // Reindex remaining images
        const reindexedImages = {};
        const reindexedPreviews = {};
        Object.keys(newInstructionImages).forEach(key => {
            const oldIndex = parseInt(key);
            const newIndex = oldIndex > index ? oldIndex - 1 : oldIndex;
            reindexedImages[newIndex] = newInstructionImages[key];
        });
        Object.keys(newInstructionImagePreviews).forEach(key => {
            const oldIndex = parseInt(key);
            const newIndex = oldIndex > index ? oldIndex - 1 : oldIndex;
            reindexedPreviews[newIndex] = newInstructionImagePreviews[key];
        });
        
        setInstructionImages(reindexedImages);
        setInstructionImagePreviews(reindexedPreviews);
    };

    const handleImageChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            setError('Please select an image file');
            return;
        }

        // Validate file size (5MB limit)
        const maxSize = 5 * 1024 * 1024;
        if (file.size > maxSize) {
            setError('File size too large. Please select an image under 5MB');
            return;
        }

        setSelectedImage(file);
        
        // Show preview
        const reader = new FileReader();
        reader.onloadend = () => {
            setImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
    };

    const handleInstructionImageChange = async (index, e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            setError('Please select an image file');
            return;
        }

        // Validate file size (5MB limit)
        const maxSize = 5 * 1024 * 1024;
        if (file.size > maxSize) {
            setError('File size too large. Please select an image under 5MB');
            return;
        }

        // Update instruction images state
        setInstructionImages(prev => ({
            ...prev,
            [index]: file
        }));

        // Show preview
        const reader = new FileReader();
        reader.onloadend = () => {
            setInstructionImagePreviews(prev => ({
                ...prev,
                [index]: reader.result
            }));
        };
        reader.onerror = () => {
            console.error('Error reading instruction image file');
        };
        reader.readAsDataURL(file);
    };

    const removeInstructionImage = (index) => {
        setInstructionImages(prev => {
            const newImages = { ...prev };
            delete newImages[index];
            return newImages;
        });

        setInstructionImagePreviews(prev => {
            const newPreviews = { ...prev };
            delete newPreviews[index];
            return newPreviews;
        });

        // Clear the imageUrl from recipe instructions
        handleInstructionChange(index, 'imageUrl', '');
    };

    const handleIngredientsPhotoChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            setError('Please select an image file');
            return;
        }

        // Validate file size (5MB limit)
        const maxSize = 5 * 1024 * 1024;
        if (file.size > maxSize) {
            setError('File size too large. Please select an image under 5MB');
            return;
        }

        setSelectedIngredientsPhoto(file);
        
        // Show preview
        const reader = new FileReader();
        reader.onloadend = () => {
            setIngredientsPhotoPreview(reader.result);
        };
        reader.readAsDataURL(file);
    };

    const removeIngredientsPhoto = () => {
        setSelectedIngredientsPhoto(null);
        setIngredientsPhotoPreview(null);
    };

    const handleCategoryChange = (category) => {
        setRecipe(prev => ({
            ...prev,
            categories: prev.categories.includes(category)
                ? prev.categories.filter(c => c !== category)
                : [...prev.categories, category]
        }));
    };

    // Photo generation function using reliable polling system (no timeouts!)
    const handleGeneratePhotos = async () => {
        try {
            setIsGeneratingPhotos(true);
            setPhotoGenerationError('');
            setPhotoGenerationSuccess('');

            console.log('Starting photo generation for recipe:', recipeId);

            // Step 1: Start the photo generation job
            const response = await axios.post(
                `${API_BASE_URL}/recipes/${recipeId}/generate-photos`,
                {
                    userID: userID,
                    generateIngredientPhoto: true,
                    generateStepPhotos: true,
                    maxStepPhotos: 5,
                    useCache: true
                },
                {
                    headers: {
                        Authorization: `Bearer ${getAuthToken()}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (!response.data.jobId) {
                throw new Error('No job ID returned from the server');
            }

            const jobId = response.data.jobId;
            console.log('Photo generation job created:', jobId);

            // Step 2: Poll for job completion
            let photoData = null;
            let attempts = 0;
            const maxAttempts = 120; // 10 minutes timeout (120 * 5 seconds = 600 seconds = 10 minutes)
            
            while (!photoData && attempts < maxAttempts) {
                attempts++;
                
                try {
                    // Wait 5 seconds between polls
                    await new Promise(resolve => setTimeout(resolve, 5000));
                    
                    const statusResponse = await axios.get(
                        `${API_BASE_URL}/recipes/photo-job-status/${jobId}`,
                        {
                            headers: {
                                Authorization: `Bearer ${getAuthToken()}`
                            }
                        }
                    );
                    
                    if (statusResponse.data.status === 'completed') {
                        photoData = statusResponse.data;
                        break;
                    } else if (statusResponse.data.status === 'failed') {
                        throw new Error(statusResponse.data.error || 'Photo generation failed');
                    }
                    
                    console.log(`Photo generation progress: ${statusResponse.data.message} (${statusResponse.data.progress}%)`);
                    
                } catch (pollError) {
                    console.error('Error polling photo generation status:', pollError);
                    if (attempts >= maxAttempts) {
                        throw new Error('Photo generation timed out after 10 minutes. Please try again later.');
                    }
                }
            }
            
            if (!photoData) {
                throw new Error('Photo generation timed out after 10 minutes. Please try again with a simpler recipe.');
            }
            
            console.log('Photo generation completed:', photoData);
            
            // Step 3: Handle successful completion
            if (photoData.result && photoData.result.recipe && photoData.result.photoResults) {
                const updatedRecipe = photoData.result.recipe;
                const photoResults = photoData.result.photoResults;

                // Update the recipe state with new photos
                setRecipe(prev => ({
                    ...prev,
                    ingredientsPhotoUrl: updatedRecipe.ingredientsPhotoUrl || prev.ingredientsPhotoUrl,
                    instructions: updatedRecipe.instructions || prev.instructions,
                    imageUrl: updatedRecipe.imageUrl || prev.imageUrl
                }));

                // Update previews for ingredients photo
                if (updatedRecipe.ingredientsPhotoUrl) {
                    setIngredientsPhotoPreview(updatedRecipe.ingredientsPhotoUrl);
                }

                // Update previews for instruction images
                const newImagePreviews = {};
                updatedRecipe.instructions.forEach((instruction, index) => {
                    if (instruction.imageUrl) {
                        newImagePreviews[index] = instruction.imageUrl;
                    }
                });
                setInstructionImagePreviews(prev => ({
                    ...prev,
                    ...newImagePreviews
                }));

                // Update main recipe image preview
                if (updatedRecipe.imageUrl && updatedRecipe.imageUrl !== recipe.imageUrl) {
                    setImagePreview(updatedRecipe.imageUrl);
                }

                setPhotoGenerationSuccess(
                    `Successfully generated ${photoResults.totalPhotosGenerated} photos! ` +
                    `${photoResults.ingredientPhotoGenerated ? 'Ingredient overview, ' : ''}` +
                    `${photoResults.stepPhotosGenerated} step photos created.`
                );

                console.log('Photo generation completed successfully:', photoResults);
                
                // Auto-refresh the page to show updated photos after 2 seconds
                setTimeout(() => {
                    window.location.reload();
                }, 2000);
                
            } else {
                throw new Error('Photo generation completed but no results received');
            }

        } catch (error) {
            console.error('Error generating photos:', error);
            
            let errorMessage = error.response?.data?.message || error.message || 'Failed to generate photos';
            
            // Handle specific error types
            if (error.response?.status === 429) {
                // Handle monthly image limit error
                const quotaError = error.response.data;
                if (quotaError.error === 'MONTHLY_IMAGE_LIMIT_EXCEEDED') {
                    const timeMessage = quotaError.timeUntilResetText 
                        ? ` You can generate AI photos again in ${quotaError.timeUntilResetText}.`
                        : ' Try again next month.';
                    errorMessage = `Monthly AI image limit reached (${quotaError.monthlyLimit} per month).${timeMessage} You can still upload your own images manually.`;
                    // Refresh image quota to show updated status
                    fetchAiImageQuota();
                } else {
                    errorMessage = quotaError.message || 'Rate limit exceeded. Please try again later.';
                }
            } else if (error.response?.status === 503) {
                errorMessage = 'Photo generation service is currently unavailable. Please try again later.';
            } else if (error.response?.status === 403) {
                errorMessage = 'You can only generate photos for your own recipes.';
            } else if (error.response?.status === 500) {
                errorMessage = 'Server error during photo generation. Please try again later.';
            }
            
            setPhotoGenerationError(errorMessage);
        } finally {
            setIsGeneratingPhotos(false);
        }
    };

    // Function to generate display text for ingredients using job-based polling (same as photo generation)
    const handleGenerateDisplayText = async () => {
        try {
            setIsGeneratingDisplayText(true);
            setDisplayTextError('');
            setDisplayTextSuccess('');

            console.log('Starting display text generation for recipe:', recipeId);

            // Step 1: Start the display text generation job
            const response = await axios.post(
                `${API_BASE_URL}/recipes/${recipeId}/generate-display-text`,
                {
                    userID: userID
                },
                {
                    headers: {
                        Authorization: `Bearer ${getAuthToken()}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (!response.data.jobId) {
                throw new Error('No job ID returned from the server');
            }

            const jobId = response.data.jobId;
            console.log('Display text generation job created:', jobId);

            // Step 2: Poll for job completion
            let displayTextData = null;
            let attempts = 0;
            const maxAttempts = 60; // 5 minutes timeout (60 * 5 seconds = 300 seconds = 5 minutes)
            
            while (!displayTextData && attempts < maxAttempts) {
                attempts++;
                
                try {
                    // Wait 5 seconds between polls
                    await new Promise(resolve => setTimeout(resolve, 5000));
                    
                    const statusResponse = await axios.get(
                        `${API_BASE_URL}/recipes/display-text-job-status/${jobId}`,
                        {
                            headers: {
                                Authorization: `Bearer ${getAuthToken()}`
                            }
                        }
                    );
                    
                    if (statusResponse.data.status === 'completed') {
                        displayTextData = statusResponse.data;
                        break;
                    } else if (statusResponse.data.status === 'failed') {
                        throw new Error(statusResponse.data.error || 'Display text generation failed');
                    }
                    
                    console.log(`Display text generation progress: ${statusResponse.data.message} (${statusResponse.data.progress}%)`);
                    
                } catch (pollError) {
                    console.error('Error polling display text generation status:', pollError);
                    if (attempts >= maxAttempts) {
                        throw new Error('Display text generation timed out after 5 minutes. Please try again later.');
                    }
                }
            }
            
            if (!displayTextData) {
                throw new Error('Display text generation timed out after 5 minutes. Please try again.');
            }
            
            console.log('Display text generation completed:', displayTextData);
            
            // Step 3: Handle successful completion
            if (displayTextData.result && displayTextData.result.success) {
                const { updatedCount, ingredients } = displayTextData.result;
                
                // Update the recipe state with new display text
                if (ingredients && Array.isArray(ingredients)) {
                    setRecipe(prev => ({
                        ...prev,
                        ingredients: ingredients
                    }));
                }

                if (updatedCount > 0) {
                    setDisplayTextSuccess(
                        `✨ Successfully generated human-readable text for ${updatedCount} ingredient${updatedCount !== 1 ? 's' : ''}! ` +
                        `Your ingredients now have a home-cooking friendly format.`
                    );
                    console.log(`✅ Display text generated for ${updatedCount} ingredients`);
                } else {
                    setDisplayTextSuccess(
                        'All ingredients already have display text or cannot be automatically converted. ' +
                        'You can manually add display text using the optional fields below.'
                    );
                }

                // Auto-hide success message after 5 seconds
                setTimeout(() => {
                    setDisplayTextSuccess('');
                }, 5000);
                
            } else {
                throw new Error(displayTextData.result?.message || 'Display text generation completed but no results received');
            }

        } catch (error) {
            console.error('Error generating display text:', error);
            
            let errorMessage = error.response?.data?.message || error.message || 'Failed to generate display text';
            
            // Handle specific error types
            if (error.response?.status === 403) {
                errorMessage = 'You can only generate display text for your own recipes.';
            } else if (error.response?.status === 404) {
                errorMessage = 'Recipe not found.';
            } else if (error.response?.status === 500) {
                errorMessage = 'Server error during display text generation. Please try again later.';
            }
            
            setDisplayTextError(errorMessage);
        } finally {
            setIsGeneratingDisplayText(false);
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (isSubmitting) return;

        setIsSubmitting(true);
        setError('');

        try {
            let imageUrl = recipe.imageUrl;

            // Handle image upload if there's a new image
            if (selectedImage) {
                const formData = new FormData();
                formData.append('image', selectedImage);

                try {
                    const uploadResponse = await axios.post(
                        `${API_BASE_URL}/recipes/upload-image`,
                        formData,
                        {
                            headers: {
                                'Content-Type': 'multipart/form-data',
                                Authorization: `Bearer ${getAuthToken()}`
                            }
                        }
                    );
                    imageUrl = uploadResponse.data.imageUrl;
                } catch (uploadError) {
                    throw new Error('Failed to upload image: ' + uploadError.message);
                }
            }

            // Handle ingredients photo upload if there's a new photo
            let ingredientsPhotoUrl = recipe.ingredientsPhotoUrl;
            if (selectedIngredientsPhoto) {
                const formData = new FormData();
                formData.append('ingredientsPhoto', selectedIngredientsPhoto);

                try {
                    const uploadResponse = await axios.post(
                        `${API_BASE_URL}/recipes/upload-ingredients-photo`,
                        formData,
                        {
                            headers: {
                                'Content-Type': 'multipart/form-data',
                                Authorization: `Bearer ${getAuthToken()}`
                            }
                        }
                    );
                    ingredientsPhotoUrl = uploadResponse.data.imageUrl;
                } catch (uploadError) {
                    throw new Error('Failed to upload ingredients photo: ' + uploadError.message);
                }
            }

            // Handle instruction image uploads
            const updatedInstructions = [...recipe.instructions];
            const uploadPromises = [];
            
            for (const index of Object.keys(instructionImages)) {
                if (instructionImages[index]) {
                    const formData = new FormData();
                    formData.append('instructionImage', instructionImages[index]);

                    const uploadPromise = axios.post(
                        `${API_BASE_URL}/recipes/upload-instruction-image`,
                        formData,
                        {
                            headers: {
                                'Content-Type': 'multipart/form-data',
                                Authorization: `Bearer ${getAuthToken()}`
                            }
                        }
                    ).then(response => {
                        // Update the instruction with the uploaded image URL
                        updatedInstructions[parseInt(index)] = {
                            ...updatedInstructions[parseInt(index)],
                            imageUrl: response.data.imageUrl
                        };
                        console.log(`Successfully uploaded instruction image ${index}:`, response.data.imageUrl);
                        return { index: parseInt(index), imageUrl: response.data.imageUrl };
                    }).catch(uploadError => {
                        console.error(`Failed to upload instruction image ${index}:`, uploadError);
                        throw new Error(`Failed to upload instruction image for step ${parseInt(index) + 1}: ${uploadError.response?.data?.message || uploadError.message}`);
                    });
                    
                    uploadPromises.push(uploadPromise);
                }
            }
            
            // Wait for all instruction image uploads to complete
            if (uploadPromises.length > 0) {
                console.log(`Uploading ${uploadPromises.length} instruction images...`);
                await Promise.all(uploadPromises);
                console.log('All instruction images uploaded successfully');
            }

            // Prepare recipe data
            const updatedRecipe = {
                ...recipe,
                instructions: updatedInstructions,
                imageUrl,
                ingredientsPhotoUrl,
                userID, // Include userID for authorization
                isVegan: recipe.isVegan,
                isVegetarian: recipe.isVegetarian,
                categories: recipe.categories
            };

            // Update recipe
            const response = await axios.put(
                `${API_BASE_URL}/recipes/${recipeId}`,
                updatedRecipe,
                {
                    headers: {
                        Authorization: `Bearer ${getAuthToken()}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.status === 200) {
                navigate(`/recipe/${recipeId}`);
            }
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Error updating recipe');
            console.error('Error updating recipe:', err);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="create-recipe">
            <h2>Edit Recipe</h2>
            {error && <div className="error-message">{error}</div>}
            
            {/* Simple loading overlay for display text generation */}
            {isGeneratingDisplayText && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        padding: '40px',
                        borderRadius: '12px',
                        maxWidth: '400px',
                        width: '90%',
                        textAlign: 'center',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
                    }}>
                        <div style={{ 
                            display: 'inline-block', 
                            width: '40px', 
                            height: '40px', 
                            border: '4px solid #f3f3f3', 
                            borderTop: '4px solid #2196F3', 
                            borderRadius: '50%', 
                            animation: 'spin 1s linear infinite',
                            marginBottom: '20px'
                        }}></div>
                        <h3 style={{ margin: '0 0 15px 0', color: '#333' }}>AI Analyzing Recipe</h3>
                        <p style={{ color: '#666', margin: 0, lineHeight: '1.5', fontSize: '16px' }}>
                            GPT is reading your recipe instructions and generating friendly ingredient descriptions. This may take 10-30 seconds...
                        </p>
                    </div>
                </div>
            )}
            
            {/* Simple loading overlay for photo generation */}
            {isGeneratingPhotos && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        padding: '40px',
                        borderRadius: '12px',
                        maxWidth: '400px',
                        width: '90%',
                        textAlign: 'center',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
                    }}>
                        <div style={{ 
                            display: 'inline-block', 
                            width: '40px', 
                            height: '40px', 
                            border: '4px solid #f3f3f3', 
                            borderTop: '4px solid #4CAF50', 
                            borderRadius: '50%', 
                            animation: 'spin 1s linear infinite',
                            marginBottom: '20px'
                        }}></div>
                        <h3 style={{ margin: '0 0 15px 0', color: '#333' }}>Generating Recipe Photos</h3>
                        <p style={{ color: '#666', margin: 0, lineHeight: '1.5', fontSize: '16px' }}>
                            AI is creating professional photos for your recipe. This may take a few minutes...
                        </p>
                    </div>
                </div>
            )}
            
            {/* Display Text Generation Section */}
            <div className="display-text-generation-section" style={{ 
                marginBottom: '30px', 
                padding: '20px', 
                border: '2px solid #e0e0e0', 
                borderRadius: '8px',
                backgroundColor: '#f0f8ff'
            }}>
                <h3 style={{ marginTop: 0, color: '#333' }}>🤖 AI-Powered Informal Ingredient Display</h3>
                <p style={{ color: '#666', marginBottom: '15px' }}>
                    Use AI to intelligently analyze your recipe and make ingredients more informal and home-cooking friendly! GPT will read your recipe instructions and convert measurements like "200g onion" into contextual text like "1 large onion, finely chopped" while keeping precise amounts in the background for cost calculations.
                </p>
                
                {displayTextError && (
                    <div style={{ 
                        color: '#d32f2f', 
                        backgroundColor: '#ffebee', 
                        padding: '12px', 
                        borderRadius: '6px', 
                        marginBottom: '15px',
                        border: '1px solid #ffcdd2'
                    }}>
                        <div style={{ marginBottom: '8px' }}>❌ {displayTextError}</div>
                        {(displayTextError.includes('timeout') || displayTextError.includes('Network error') || displayTextError.includes('may still be processing')) && (
                            <button
                                type="button"
                                onClick={() => window.location.reload()}
                                style={{
                                    backgroundColor: '#2196F3',
                                    color: 'white',
                                    border: 'none',
                                    padding: '6px 12px',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    cursor: 'pointer',
                                    marginTop: '8px'
                                }}
                            >
                                🔄 Refresh Page to Check
                            </button>
                        )}
                    </div>
                )}
                
                {displayTextSuccess && (
                    <div style={{ 
                        color: '#2e7d32', 
                        backgroundColor: '#e8f5e8', 
                        padding: '12px', 
                        borderRadius: '6px', 
                        marginBottom: '15px',
                        border: '1px solid #c8e6c9'
                    }}>
                        ✅ {displayTextSuccess}
                    </div>
                )}
                
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '15px' }}>
                    <button
                        type="button"
                        onClick={handleGenerateDisplayText}
                        disabled={isGeneratingDisplayText}
                        style={{
                            backgroundColor: isGeneratingDisplayText ? '#cccccc' : '#2196F3',
                            color: 'white',
                            border: 'none',
                            padding: '12px 24px',
                            borderRadius: '6px',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            cursor: isGeneratingDisplayText ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                    >
                        {isGeneratingDisplayText ? (
                            <>
                                <span style={{ 
                                    display: 'inline-block', 
                                    width: '16px', 
                                    height: '16px', 
                                    border: '2px solid #ffffff', 
                                    borderTop: '2px solid transparent', 
                                    borderRadius: '50%', 
                                    animation: 'spin 1s linear infinite' 
                                }}></span>
                                AI Analyzing Recipe...
                            </>
                        ) : (
                            <>
                                🤖 Generate with AI
                            </>
                        )}
                    </button>
                    
                    <div style={{ fontSize: '14px', color: '#666', lineHeight: '1.6', flex: 1 }}>
                        <strong>How AI helps:</strong><br />
                        • Reads your recipe instructions for context<br />
                        • Converts "200g onion" → "1 large onion, finely chopped"<br />
                        • Converts "15g garlic" → "3 cloves garlic, minced"<br />
                        • Adds appropriate prep details based on cooking method<br />
                        • Makes recipes feel natural and home-cook friendly!<br />
                        <em style={{ fontSize: '12px', color: '#999' }}>
                            Note: Precise amounts stay in background for cost calculations
                        </em>
                    </div>
                </div>
            </div>
            
            {/* Photo Generation Section */}
            {photoGenerationAvailable && (
                <div className="photo-generation-section" style={{ 
                    marginBottom: '30px', 
                    padding: '20px', 
                    border: '2px solid #e0e0e0', 
                    borderRadius: '8px',
                    backgroundColor: '#f9f9f9'
                }}>
                    <h3 style={{ marginTop: 0, color: '#333' }}>🎨 AI Photo Generation</h3>
                    <p style={{ color: '#666', marginBottom: '15px' }}>
                        Generate professional photos for your recipe including ingredient overview and step-by-step cooking photos.
                    </p>
                    
                    {/* AI Image Quota Display for Edit Recipe */}
                    {aiImageQuota && (
                        <div style={{ 
                            marginBottom: '15px', 
                            padding: '12px', 
                            backgroundColor: '#fff3e0', 
                            borderRadius: '6px',
                            border: '1px solid #ff9800'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                                <span style={{ fontWeight: 'bold', color: '#e65100' }}>📸 Monthly AI Image Limit:</span>
                                <button 
                                    onClick={fetchAiImageQuota}
                                    disabled={imageQuotaLoading}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        fontSize: '16px'
                                    }}
                                    title="Refresh image quota status"
                                >
                                    {imageQuotaLoading ? '🔄' : '🔄'}
                                </button>
                            </div>
                            {aiImageQuota.isPowerUser ? (
                                <span style={{ color: '#4caf50', fontWeight: 'bold' }}>✨ Unlimited (Power User)</span>
                            ) : (
                                <div>
                                    <div style={{ marginBottom: '4px' }}>
                                        <span style={{ color: '#e65100' }}>
                                            {aiImageQuota.used} / {aiImageQuota.monthlyLimit} used this month
                                            {aiImageQuota.remaining === 0 && (
                                                <span style={{ color: '#d32f2f', fontWeight: 'bold' }}> - Limit reached</span>
                                            )}
                                        </span>
                                    </div>
                                    {aiImageQuota.remaining > 0 && (
                                        <div style={{ color: '#388e3c', fontSize: '14px' }}>
                                            Remaining: {aiImageQuota.remaining}
                                        </div>
                                    )}
                                    <div style={{ color: '#666', fontSize: '12px', marginTop: '4px' }}>
                                        Resets: {aiImageQuota.nextResetDate}
                                        {aiImageQuota.timeUntilResetText && (
                                            <span> ({aiImageQuota.timeUntilResetText})</span>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    
                    {photoGenerationError && (
                        <div style={{ marginBottom: '15px' }}>
                            <div style={{ 
                                color: '#d32f2f', 
                                backgroundColor: '#ffebee', 
                                padding: '15px', 
                                borderRadius: '6px', 
                                marginBottom: '15px',
                                border: '1px solid #ffcdd2',
                                whiteSpace: 'pre-line',
                                textAlign: 'left'
                            }}>
                                ❌ {photoGenerationError}
                            </div>
                            
                            <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
                                <button
                                    type="button"
                                    onClick={handleGeneratePhotos}
                                    disabled={aiImageQuota && !aiImageQuota.isPowerUser && aiImageQuota.remaining <= 0}
                                    style={{
                                        backgroundColor: (aiImageQuota && !aiImageQuota.isPowerUser && aiImageQuota.remaining <= 0) ? '#cccccc' : '#4CAF50',
                                        color: 'white',
                                        border: 'none',
                                        padding: '12px 24px',
                                        borderRadius: '6px',
                                        fontSize: '16px',
                                        cursor: (aiImageQuota && !aiImageQuota.isPowerUser && aiImageQuota.remaining <= 0) ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    Try Again
                                </button>
                            </div>
                        </div>
                    )}
                    
                    {photoGenerationSuccess && (
                        <div style={{ 
                            color: '#2e7d32', 
                            backgroundColor: '#e8f5e8', 
                            padding: '10px', 
                            borderRadius: '4px', 
                            marginBottom: '15px',
                            border: '1px solid #c8e6c9'
                        }}>
                            ✅ {photoGenerationSuccess}
                        </div>
                    )}
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        <button
                            type="button"
                            onClick={handleGeneratePhotos}
                            disabled={isGeneratingPhotos || (aiImageQuota && !aiImageQuota.isPowerUser && aiImageQuota.remaining <= 0)}
                            style={{
                                backgroundColor: (isGeneratingPhotos || (aiImageQuota && !aiImageQuota.isPowerUser && aiImageQuota.remaining <= 0)) ? '#cccccc' : '#4CAF50',
                                color: 'white',
                                border: 'none',
                                padding: '12px 24px',
                                borderRadius: '6px',
                                fontSize: '16px',
                                fontWeight: 'bold',
                                cursor: (isGeneratingPhotos || (aiImageQuota && !aiImageQuota.isPowerUser && aiImageQuota.remaining <= 0)) ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}
                        >
                            {isGeneratingPhotos ? (
                                <>
                                    <span style={{ 
                                        display: 'inline-block', 
                                        width: '16px', 
                                        height: '16px', 
                                        border: '2px solid #ffffff', 
                                        borderTop: '2px solid transparent', 
                                        borderRadius: '50%', 
                                        animation: 'spin 1s linear infinite' 
                                    }}></span>
                                    Generating Photos...
                                </>
                            ) : aiImageQuota && !aiImageQuota.isPowerUser && aiImageQuota.remaining <= 0 ? (
                                <>
                                    🚫 Monthly Limit Reached
                                </>
                            ) : (
                                <>
                                    📸 Generate Recipe Photos
                                </>
                            )}
                        </button>
                        
                        <div style={{ fontSize: '14px', color: '#666' }}>
                            {aiImageQuota && !aiImageQuota.isPowerUser && aiImageQuota.remaining <= 0 ? (
                                <>
                                    Monthly AI image limit reached. Resets on {aiImageQuota.nextResetDate}. 
                                    You can still upload your own images manually.
                                </>
                            ) : (
                                'This will generate an ingredient overview photo and up to 5 step photos for your recipe.'
                            )}
                        </div>
                    </div>
                    
                </div>
            )}
            
            <form onSubmit={handleSubmit}>
                <div className="form-group">
                    <label htmlFor="name">Name</label>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        value={recipe.name}
                        onChange={handleChange}
                        required
                    />
                </div>

                <div className="dietary-preferences-container">
                    <h3 className="section-header">Dietary Preferences</h3>
                    <div className="dietary-preferences">
                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                name="isVegan"
                                checked={recipe.isVegan}
                                onChange={handleChange}
                            />
                            Vegan
                        </label>
                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                name="isVegetarian"
                                checked={recipe.isVegetarian}
                                onChange={handleChange}
                            />
                            Vegetarian
                        </label>
                    </div>
                </div>

                <div className="categories-container">
                    <h3 className="section-header">Categories</h3>
                    <div className="categories-grid">
                        {AVAILABLE_CATEGORIES.map(category => {
                            const displayName = categoryDisplayNames[category] || 
                                category.charAt(0).toUpperCase() + category.slice(1);
                            return (
                                <label 
                                    key={category} 
                                    className={`category-checkbox ${recipe.categories.includes(category) ? 'selected' : ''}`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={recipe.categories.includes(category)}
                                        onChange={() => handleCategoryChange(category)}
                                    />
                                    {displayName}
                                </label>
                            );
                        })}
                    </div>
                </div>

                <div className="form-group">
                    <label>Ingredients</label>
                    {recipe.ingredients.map((ingredient, index) => (
                        <RecipeIngredientInput
                            key={index}
                            ingredient={ingredient}
                            index={index}
                            handleIngredientChange={handleIngredientChange}
                            removeIngredient={removeIngredient}
                        />
                    ))}
                    <button 
                        type="button" 
                        onClick={addIngredient}
                        className="button-secondary"
                    >
                        Add Ingredient
                    </button>
                </div>

                {/* Ingredients Photo Upload */}
                <div className="form-group">
                    <label>Ingredients Photo (Optional)</label>
                    <p style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
                        Upload a photo showing all the ingredients needed for this recipe
                    </p>
                    <div className="image-upload-container">
                        {ingredientsPhotoPreview && (
                            <div className="image-preview">
                                <img 
                                    src={ingredientsPhotoPreview}
                                    alt="Ingredients preview"
                                    style={{ 
                                        maxWidth: '200px',
                                        maxHeight: '200px',
                                        objectFit: 'cover',
                                        marginBottom: '10px',
                                        borderRadius: '8px',
                                        border: '1px solid #ddd'
                                    }}
                                />
                                <button 
                                    type="button"
                                    onClick={removeIngredientsPhoto}
                                    style={{
                                        marginLeft: '10px',
                                        padding: '4px 8px',
                                        backgroundColor: '#dc3545',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        fontSize: '12px'
                                    }}
                                >
                                    Remove Photo
                                </button>
                            </div>
                        )}
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handleIngredientsPhotoChange}
                            className="image-upload-input"
                        />
                        <p className="or-divider">OR</p>
                        <input
                            type="text"
                            name="ingredientsPhotoUrl"
                            value={recipe.ingredientsPhotoUrl}
                            onChange={handleChange}
                            placeholder="Enter ingredients photo URL (optional)"
                            className="image-url-input"
                        />
                        <small style={{ color: '#666', marginTop: '5px' }}>
                            You can skip adding an ingredients photo if you don't have one available.
                        </small>
                    </div>
                </div>

                <div className="form-group">
                    <label>Instructions</label>
                    {recipe.instructions.map((instruction, index) => (
                        <div key={index} className="instruction-input-container" style={{ marginBottom: '20px', padding: '15px', border: '1px solid #ddd', borderRadius: '8px' }}>
                            <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                                <span style={{ fontWeight: 'bold', minWidth: '30px' }}>{index + 1}.</span>
                                <textarea
                                    value={instruction.text || instruction || ''}
                                    onChange={(e) => handleInstructionChange(index, 'text', e.target.value)}
                                    placeholder={`Step ${index + 1} instructions...`}
                                    style={{ flex: 1, minHeight: '60px' }}
                                />
                                {recipe.instructions.length > 1 && (
                                    <button 
                                        type="button" 
                                        onClick={() => removeInstruction(index)}
                                        className="button-danger"
                                        style={{ padding: '4px 8px', height: 'fit-content' }}
                                    >
                                        Remove
                                    </button>
                                )}
                            </div>
                            
                            {/* Instruction Image Upload */}
                            <div className="instruction-image-section" style={{ marginTop: '10px' }}>
                                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
                                    Step Image (Optional):
                                </label>
                                
                                {(instructionImagePreviews[index] || (instruction.imageUrl && instruction.imageUrl !== '')) && (
                                    <div style={{ marginBottom: '10px' }}>
                                        <img 
                                            src={instructionImagePreviews[index] || instruction.imageUrl} 
                                            alt={`Step ${index + 1}`}
                                            style={{ 
                                                maxWidth: '200px', 
                                                maxHeight: '150px', 
                                                objectFit: 'cover',
                                                borderRadius: '4px',
                                                border: '1px solid #ddd'
                                            }}
                                        />
                                        <button 
                                            type="button"
                                            onClick={() => removeInstructionImage(index)}
                                            style={{
                                                marginLeft: '10px',
                                                padding: '4px 8px',
                                                backgroundColor: '#dc3545',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '4px',
                                                fontSize: '12px'
                                            }}
                                        >
                                            Remove Image
                                        </button>
                                    </div>
                                )}
                                
                                {/* Image URL Input */}
                                <div style={{ marginBottom: '10px' }}>
                                    <label style={{ display: 'block', marginBottom: '3px', fontSize: '12px', color: '#666' }}>
                                        Image URL:
                                    </label>
                                    <input
                                        type="url"
                                        placeholder="https://example.com/image.jpg"
                                        value={instruction.imageUrl || ''}
                                        onChange={(e) => handleInstructionChange(index, 'imageUrl', e.target.value)}
                                        style={{ 
                                            width: '100%', 
                                            padding: '8px', 
                                            border: '1px solid #ddd', 
                                            borderRadius: '4px',
                                            fontSize: '14px'
                                        }}
                                    />
                                </div>
                                
                                {/* File Upload */}
                                <div style={{ marginBottom: '10px' }}>
                                    <label style={{ display: 'block', marginBottom: '3px', fontSize: '12px', color: '#666' }}>
                                        Or upload file:
                                    </label>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => handleInstructionImageChange(index, e)}
                                        style={{ fontSize: '14px' }}
                                    />
                                </div>
                                
                                <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                                    Add an image URL or upload a file to help illustrate this step
                                </small>
                            </div>
                        </div>
                    ))}
                    <button 
                        type="button" 
                        onClick={addInstruction}
                        className="button-secondary"
                    >
                        Add Step
                    </button>
                </div>

                <div className="form-group">
                    <label>Recipe Image</label>
                    <div className="image-upload-container">
                        {imagePreview && (
                            <div className="image-preview">
                                <RecipeImage 
                                    imageUrl={imagePreview}
                                    alt="Recipe preview"
                                    style={{ 
                                        maxWidth: '200px',
                                        maxHeight: '200px',
                                        objectFit: 'cover',
                                        marginBottom: '10px'
                                    }}
                                />
                            </div>
                        )}
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageChange}
                            className="image-upload-input"
                        />
                        <p className="or-divider">OR</p>
                        <input
                            type="text"
                            name="imageUrl"
                            value={recipe.imageUrl}
                            onChange={handleChange}
                            placeholder="Enter image URL"
                            className="image-url-input"
                        />
                    </div>
                </div>

                <div className="form-group">
                    <label htmlFor="cookingTime">Cooking Time (minutes)</label>
                    <input 
                        type="number" 
                        id="cookingTime" 
                        name="cookingTime" 
                        value={recipe.cookingTime}
                        onChange={handleChange} 
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="servings">Servings</label>
                    <input
                        type="number"
                        id="servings"
                        name="servings"
                        min="1"
                        value={recipe.servings}
                        onChange={handleChange}
                    />
                </div>

                <button 
                    type="submit" 
                    className="button-primary"
                    disabled={isSubmitting}
                >
                    {isSubmitting ? 'Updating...' : 'Update Recipe'}
                </button>
            </form>
        </div>
    );
}
